import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import { DimensionBar } from "@/components/DimensionBar";
import { useAuth } from "@/context/AuthContext";
import { SharePopup } from "@/components/share/SharePopup";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DimDots, dimsInText, SevenDotsLine, ConnectsRow, PracticeCards } from "@/components/DimDots";
import { Download, RefreshCw, Mountain, Ghost, Wind, Radio, Sparkles, ChevronDown, Info, MessageSquare } from "lucide-react";

const SAFETY_NOTE = "Caesar's Ghost is a self-reflection tool, not a diagnosis or clinical evaluation. Your results describe patterns at this point in time; they are not fixed labels.";

const SECTIONS = [
  { key: "ground", title: "Your Ground", icon: Mountain, blurb: "What seems to help you make your own choices." },
  { key: "ghosts", title: "Your Ghosts", icon: Ghost, blurb: "Influences or habits that may be worth a closer look." },
  { key: "shifts", title: "Your Shifts", icon: Wind, blurb: "Situations where your authority might change." },
  { key: "signals", title: "Your Signals", icon: Radio, blurb: "Things you may want to notice when making a choice." },
];

export default function Portrait() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [content, setContent] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showDims] = useState(false);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/me/portrait/${sessionId}`);
      setData(res.data);
      const [c, f] = await Promise.allSettled([
        api.get("/content/participant"),
        api.get(`/pilot/feedback/${sessionId}`),
      ]);
      if (c.status === "fulfilled") setContent(c.value.data);
      if (f.status === "fulfilled") setFeedback(f.value.data.feedback);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const retry = async () => {
    setRetrying(true);
    try {
      const res = await api.post(`/me/portrait/${sessionId}/retry-ai`);
      if (res.data.ok) { toast.success("Interpretation ready"); await load(); }
      else toast.error(res.data.error || "Still unavailable");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRetrying(false); }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("cg_token");
      const r = await fetch(`${API}/me/report/${sessionId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Report not ready");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "authority-portrait.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Report not ready yet"); }
  };

  if (loading) return (<div><HeaderNav /><div className="max-w-3xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Composing your portrait…</div></div>);
  if (!data) return (<div><HeaderNav /><div className="max-w-3xl mx-auto px-5 pt-24 text-[#9CA3AF]">Portrait not found.</div></div>);

  const dims = data.dimensions || [];
  const scores = data.scores?.dimension_scores || {};
  const rel = data.scores?.relative || {};
  const archetype = data.scores?.archetype;
  const content_ai = data.interpretation?.content;
  const isDemo = data.session?.is_demo;
  const aiReady = data.ai_status === "ready" && content_ai;
  const first = user?.first_name || user?.name?.split(" ")[0] || "";
  const explanations = content?.dimension_explanations || {};
  const privacyText = (content?.privacy_statement || "This Authority Portrait is private to you.")
    .replace("{first_name}", first || "This portrait");

  return (
    <div>
      <HeaderNav />
      {data.session?.completed_at && <SharePopup userId={user?.id} portraitKey={sessionId} source="portrait" />}
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-12 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-4 cg-fade-up">
          <div>
            <p className="cg-eyebrow mb-3">Authority Portrait</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#F3F4F6]" data-testid="portrait-title">
              {first ? `${first}'s Authority Portrait` : "Your Authority Portrait"}
            </h1>
            <p className="mt-2 text-[#9CA3AF]">Where your authority lies.</p>
            <SevenDotsLine />
            {archetype && (
              <p className="mt-3 text-[#9CA3AF]">Configuration: <span className="text-brass font-medium">{archetype.name}</span>
                <span className="ml-2 text-xs text-[#6B7280]">(descriptive, not a ranking)</span></p>
            )}
          </div>
          <div className="flex gap-3">
            <button data-testid="download-pdf-button" onClick={downloadPdf}
              className="inline-flex items-center gap-2 border border-[rgba(255,255,255,0.15)] text-[#F3F4F6] px-5 py-2.5 rounded-full cg-scale-btn hover:border-brass text-sm">
              <Download className="w-4 h-4" /> Download PDF
            </button>
            <Link to="/dashboard" data-testid="to-dashboard-link"
              className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-brass px-3 py-2.5">Dashboard</Link>
          </div>
        </div>

        {isDemo && <p className="mt-6 cg-eyebrow" data-testid="demo-banner">Demo / synthetic data — not validated science</p>}

        {/* How to read your portrait */}
        {content && (
          <div className="cg-card p-7 mt-8 cg-fade-up" data-testid="how-to-read">
            <div className="flex items-center gap-2 mb-3"><Info className="w-5 h-5 text-brass" /><h2 className="font-serif text-2xl text-[#F3F4F6]">How to Read Your Authority Portrait</h2></div>
            <div className="text-[#B9BDC7] leading-relaxed space-y-3">
              {(content.how_to_read || "").split("\n").filter((p) => p.trim()).map((p, i) => <p key={i}>{p}</p>)}
              <p className="text-[#E8EAEE]" data-testid="portrait-safety-note">{SAFETY_NOTE}</p>
            </div>
          </div>
        )}

        {data.scores?.normative && (
          <div className="cg-card p-7 mt-8" data-testid="portrait-normative">
            <h2 className="font-serif text-2xl text-[#F3F4F6] mb-1">Normative comparison</h2>
            <p className="text-sm text-[#6B7280] mb-4">Relative to approved norm group: {data.scores.normative.norm_group}</p>
            {Object.entries(data.scores.normative.dimensions || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm border-b border-[var(--cg-border)] py-1.5">
                <span className="text-[#9CA3AF]">{scores[k]?.name || k}</span>
                <span className="font-mono text-[#E8EAEE]">{v.percentile}th percentile · z {v.z}</span>
              </div>
            ))}
          </div>
        )}

        {/* dimensions */}
        <div className="cg-card p-7 mt-8 cg-fade-up" data-testid="portrait-dimensions">
          <h2 className="font-serif text-2xl text-[#F3F4F6] mb-1">Your Seven Dimensions</h2>
          <p className="text-sm text-[#6B7280] mb-4">A multidimensional portrait, read together — there is no single authority score, and longer or shorter bars are not better or worse.</p>
          {dims.map((d) => scores[d.key] && (
            <DimensionBar key={d.key} dimKey={d.key} name={d.name}
              aligned={scores[d.key].aligned_mean}
              testId={`dim-${d.key}`} />
          ))}
          <div className="mt-6 pt-5 border-t border-[var(--cg-border)]" data-testid="dimension-explanations">
            <h3 className="font-serif text-xl text-[#F3F4F6] mb-1">What Each Dot Means</h3>
            <p className="text-sm text-[#6B7280] mb-4">Throughout your Portrait, a coloured dot shows which dimension a section is drawing on.</p>
            <div className="space-y-4">
              {dims.map((d) => explanations[d.key] && (
                <div key={d.key} className="grid sm:grid-cols-[220px_1fr] gap-1 sm:gap-4">
                  <DimDots keys={[d.key]} />
                  <p className="text-sm text-[#9CA3AF] leading-relaxed">{explanations[d.key]}</p>
                </div>
              ))}
            </div>
          </div>
          <ConnectsRow steps={["Your seven dimensions", "Ground · Ghosts · Shifts · Signals", "Reclamation & questions", "Experiments & C³"]} />
        </div>

        {!aiReady ? (
          <div className="cg-card p-8 mt-6 text-center cg-fade" data-testid="ai-pending">
            <p className="text-[#F3F4F6] font-serif text-2xl mb-2">Your scores are saved.</p>
            <p className="text-sm text-[#9CA3AF] mb-6 max-w-md mx-auto">
              The reflective interpretation is temporarily unavailable. Your responses and dimension results are
              safely stored and unchanged — you can generate the narrative when you're ready.
            </p>
            <button data-testid="retry-ai-button" onClick={retry} disabled={retrying}
              className="inline-flex items-center gap-2 bg-brass text-obsidian font-medium px-6 py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
              <RefreshCw className={`w-4 h-4 ${retrying ? "animate-spin" : ""}`} /> {retrying ? "Generating…" : "Generate interpretation"}
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            <section className="cg-card p-8 cg-fade-up" data-testid="portrait-narrative">
              <h2 className="font-serif text-3xl text-brass mb-3">Your Authority Portrait</h2>
              <div className="mb-4"><DimDots keys={dimsInText(content_ai.authority_portrait)} testId="dots-authority_portrait" /></div>
              <p className="text-[#D6D9DF] leading-[1.9] whitespace-pre-line">{content_ai.authority_portrait}</p>
            </section>

            <div className="grid md:grid-cols-2 gap-6">
              {SECTIONS.map((s, i) => (
                <section key={s.key} className={`cg-card p-7 cg-fade-up ${s.key === "ghosts" ? "bg-[#0A0B0E] border-[rgba(217,162,95,0.28)]" : ""}`} style={{ animationDelay: `${i * 60}ms` }} data-testid={`section-${s.key}`}>
                  <s.icon className="w-6 h-6 text-brass mb-3" />
                  <h3 className="font-serif text-2xl text-[#F3F4F6] mb-1">{s.title}</h3>
                  <p className="cg-eyebrow mb-3">{s.blurb}</p>
                  <div className="mb-4"><DimDots keys={dimsInText(content_ai[s.key])} testId={`dots-${s.key}`} /></div>
                  <p className="text-[#B9BDC7] leading-relaxed text-[0.95rem] whitespace-pre-line">{content_ai[s.key]}</p>
                </section>
              ))}
            </div>

            <section className="cg-card p-8 cg-fade-up" data-testid="section-reclamation">
              <Sparkles className="w-6 h-6 text-brass mb-3" />
              <h3 className="font-serif text-2xl text-[#F3F4F6] mb-1">Your Reclamation</h3>
              <p className="cg-eyebrow mb-4">Practical ways to make choices more consciously</p>
              <PracticeCards items={content_ai.reclamation || []} label="RECLAMATION" testId="dots-reclamation" />
            </section>

            <section className="cg-card p-8 cg-fade-up" data-testid="section-questions">
              <h3 className="font-serif text-2xl text-[#F3F4F6] mb-4">Questions Worth Sitting With</h3>
              <div className="space-y-3">
                {(content_ai.questions_worth_sitting_with || []).map((q, i) => (
                  <p key={i} className="font-serif text-xl italic text-[#D6D9DF] bg-[#0D0E12] border-l-2 border-brass rounded-r-xl px-5 py-3">"{q}"</p>
                ))}
              </div>
            </section>

            <section className="cg-card p-8 cg-fade-up" data-testid="section-experiments">
              <h3 className="font-serif text-2xl text-[#F3F4F6] mb-4">Experiments</h3>
              <PracticeCards items={content_ai.experiments || []} label="EXPERIMENT" testId="dots-experiments" accent="#A8CBB0" />
            </section>

            {(content_ai.c3_prompts || []).length > 0 && (
              <section className="cg-card p-8 cg-fade-up" data-testid="section-c3">
                <p className="cg-eyebrow mb-2">Developmental Framework</p>
                <h3 className="font-serif text-3xl text-brass mb-4">C³ · Clarify → Choose → Condition</h3>
                <div className="text-sm text-[#B9BDC7] leading-relaxed mb-6 space-y-1" data-testid="c3-explainer">
                  <p>Use C³ with one real decision.</p>
                  <p><span className="text-[#F3F4F6]">Clarify:</span> What matters most to me? What is influencing my choice?</p>
                  <p><span className="text-[#F3F4F6]">Choose:</span> What do I want to decide? Whose advice do I want to consider?</p>
                  <p><span className="text-[#F3F4F6]">Condition:</span> What support, time or practice would help me follow through?</p>
                </div>
                <div className="space-y-6">
                  {content_ai.c3_prompts.map((p, i) => (
                    <div key={i} className="space-y-3">
                    {(p.dimension || "").toLowerCase().startsWith("example") && (
                      <p className="font-serif italic text-lg text-[#E8EAEE]" data-testid={`c3-example-title-${i}`}>{p.dimension}</p>
                    )}
                    <DimDots keys={dimsInText(p.dimension, p.clarify, p.choose, p.condition)} testId={`dots-c3-${i}`} />
                    <div className="grid sm:grid-cols-3 gap-4">
                      {["clarify", "choose", "condition"].map((step) => (
                        <div key={step} className="bg-[#0D0E12] rounded-xl p-5 border border-[var(--cg-border)]">
                          <p className="cg-eyebrow mb-2">{step}</p>
                          <p className="text-sm text-[#B9BDC7] leading-relaxed">{p[step]}</p>
                        </div>
                      ))}
                    </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <FeedbackCard sessionId={sessionId} existing={feedback} />
          </div>
        )}
        <p className="mt-10 text-xs text-[#6B7280] leading-relaxed text-center max-w-2xl mx-auto" data-testid="portrait-privacy">
          {privacyText}
        </p>
      </div>
    </div>
  );
}

function FeedbackCard({ sessionId, existing }) {
  const [recognizable, setRecognizable] = useState(existing?.recognizable || 0);
  const [confusing, setConfusing] = useState(existing?.confusing || "");
  const [inaccurate, setInaccurate] = useState(existing?.inaccurate || "");
  const [surprised, setSurprised] = useState(existing?.surprised || "");
  const [difficult, setDifficult] = useState(existing?.difficult_items || "");
  const [finalComments, setFinalComments] = useState(existing?.final_comments || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(!!existing);
  const [formVisible, setFormVisible] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/pilot/feedback", {
        session_id: sessionId,
        recognizable: recognizable || null,
        confusing, inaccurate, surprised,
        difficult_items: difficult, final_comments: finalComments,
      });
      setDone(true);
      setFormVisible(false);
      toast.success("Thank you — your feedback has been received");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const scaleLabels = ["Not at all", "A little", "Somewhat", "Mostly", "Very much"];
  const ta = "w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2 text-sm text-[#E8EAEE] focus:border-brass outline-none";

  return (
    <section className="cg-card p-8 cg-fade-up" data-testid="feedback-invite">
      <div className="flex items-center gap-2 mb-1"><MessageSquare className="w-5 h-5 text-brass" /><h3 className="font-serif text-2xl text-[#F3F4F6]">We Value Your Feedback</h3></div>
      <p className="text-sm text-[#6B7280] mb-5">Your experience helps us continue to strengthen Caesar's Ghost. If you have a few minutes, we would value your feedback on your assessment experience and Authority Portrait. This is separate from your assessment and never changes your Portrait.</p>

      {done && !formVisible ? (
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid="feedback-done">
          <p className="text-sm text-[#7D9D86]">Thank you — your feedback has been received.</p>
          <button data-testid="feedback-edit" onClick={() => setFormVisible(true)} className="text-sm text-brass hover:opacity-80">Edit my feedback</button>
        </div>
      ) : !formVisible ? (
        <button data-testid="share-feedback-button" onClick={() => setFormVisible(true)}
          className="bg-brass text-obsidian font-medium px-6 py-2.5 rounded-full cg-scale-btn hover:bg-amber2">
          Share Your Feedback
        </button>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-sm text-[#E8EAEE] mb-2">Did this Authority Portrait feel recognizable to you?</p>
            <div className="flex flex-wrap gap-2" data-testid="feedback-recognizable">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`feedback-recognizable-${n}`} onClick={() => setRecognizable(n)}
                  className={`px-3 py-2 rounded-lg border text-xs cg-scale-btn ${recognizable === n ? "border-brass bg-[rgba(217,162,95,0.12)] text-[#F3F4F6]" : "border-[var(--cg-border)] text-[#9CA3AF] hover:border-brass/50"}`}>
                  <span className="font-mono">{n}</span> · {scaleLabels[n - 1]}
                </button>
              ))}
            </div>
          </div>
          <label className="block"><span className="text-sm text-[#E8EAEE]">Was anything confusing or difficult to understand?</span>
            <textarea data-testid="feedback-confusing" rows={2} className={`${ta} mt-1.5`} value={confusing} onChange={(e) => setConfusing(e.target.value)} /></label>
          <label className="block"><span className="text-sm text-[#E8EAEE]">Was there anything in your Portrait that felt inaccurate or unlike you?</span>
            <textarea data-testid="feedback-inaccurate" rows={2} className={`${ta} mt-1.5`} value={inaccurate} onChange={(e) => setInaccurate(e.target.value)} /></label>
          <label className="block"><span className="text-sm text-[#E8EAEE]">Was there anything that surprised you?</span>
            <textarea data-testid="feedback-surprised" rows={2} className={`${ta} mt-1.5`} value={surprised} onChange={(e) => setSurprised(e.target.value)} /></label>
          <label className="block"><span className="text-sm text-[#E8EAEE]">Were there any assessment statements you found difficult to answer?</span>
            <textarea data-testid="feedback-difficult" rows={2} className={`${ta} mt-1.5`} value={difficult} onChange={(e) => setDifficult(e.target.value)} /></label>
          <label className="block"><span className="text-sm text-[#E8EAEE]">Any final comments?</span>
            <textarea data-testid="feedback-final" rows={2} className={`${ta} mt-1.5`} value={finalComments} onChange={(e) => setFinalComments(e.target.value)} /></label>
          <button data-testid="feedback-submit" onClick={submit} disabled={busy}
            className="bg-brass text-obsidian font-medium px-6 py-2.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-50">
            {busy ? "Sending…" : "Share Your Feedback"}
          </button>
        </div>
      )}
    </section>
  );
}
